"use client"

import {useEffect, useMemo} from "react";
import {useBanReasonQuery, useCheckRegionRestrictionQuery} from "@/lib/api/auth/authApi";
import Cookies from "js-cookie";
import {useRouter} from "@/i18n/navigation";
import {CookiesKeysEnum} from "@/lib/enums/CookiesKeysEnum";
import {useAppSelector} from "@/lib/hooks";
import {useOpenDialog} from "@/lib/dialog-orchestrator/react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import Script from "next/script";
import {useDispatch} from "react-redux";
import {setIsAnjReady, setIsCaptchaReady} from "@/lib/slices/uiSlice";
import {
    AUTH_SESSION_ID_KEY,
    AUTH_SESSION_USER_ID_KEY,
    WELCOME_BONUS_SHOWN_SESSION_ID_KEY,
    UNLOCK_NEXT_BONUS_SHOWN_SESSION_ID_KEY,
    PUSH_PROMPT_SHOWN_SESSION_ID_KEY
} from "@/lib/utils/contants/welcomeBonusStorage";
import {safeSessionStorage} from "@/lib/utils/storage";

export const AuthInitializer = () => {
    const userId = useAppSelector(state => state.auth.userId);
    const router = useRouter();
    const dispatch = useDispatch();
    const openDialog = useOpenDialog();

    const {isError: isRegionRestrictionError, error: regionRestrictionError} = useCheckRegionRestrictionQuery();
    const {data: banReason} = useBanReasonQuery(userId!, {skip: !userId});

    useEffect(() => {
        history.scrollRestoration = 'manual';
    });

    useEffect(() => {
        if (!!banReason) {
            Cookies.set(CookiesKeysEnum.BANNED_USER_ID, userId!, {expires: 30});
            router.push('/banned');
        }
    }, [banReason, router, userId]);

    const country = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-expect-error
        return regionRestrictionError?.data?.message?.split(" ")[0]
    }, [regionRestrictionError])

    const ip = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-expect-error
        return regionRestrictionError?.data?.message?.split(" ")[1]
    }, [regionRestrictionError])

    // REGIONAL_RESTRICTIONS is persistent + replace-mode; the engine dedupes
    // repeated requests onto the live instance, so opening on the error is enough.
    useEffect(() => {
        if (!isRegionRestrictionError) return;
        openDialog(DialogsEnum.REGIONAL_RESTRICTIONS, {props: {country, ip}});
    }, [isRegionRestrictionError, country, ip, openDialog]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        if (!userId) {
            safeSessionStorage.removeItem(AUTH_SESSION_USER_ID_KEY);
            safeSessionStorage.removeItem(AUTH_SESSION_ID_KEY);
            safeSessionStorage.removeItem(WELCOME_BONUS_SHOWN_SESSION_ID_KEY);
            safeSessionStorage.removeItem(UNLOCK_NEXT_BONUS_SHOWN_SESSION_ID_KEY);
            safeSessionStorage.removeItem(PUSH_PROMPT_SHOWN_SESSION_ID_KEY);
            return;
        }

        const storedUserId = safeSessionStorage.getItem(AUTH_SESSION_USER_ID_KEY);
        const storedSessionId = safeSessionStorage.getItem(AUTH_SESSION_ID_KEY);
        if (storedUserId !== userId || !storedSessionId) {
            safeSessionStorage.setItem(AUTH_SESSION_USER_ID_KEY, userId);
            safeSessionStorage.setItem(AUTH_SESSION_ID_KEY, `${userId}-${Date.now()}`);
            safeSessionStorage.removeItem(WELCOME_BONUS_SHOWN_SESSION_ID_KEY);
            safeSessionStorage.removeItem(UNLOCK_NEXT_BONUS_SHOWN_SESSION_ID_KEY);
            safeSessionStorage.removeItem(PUSH_PROMPT_SHOWN_SESSION_ID_KEY);
        }
    }, [userId]);


    return (
        <>
            <Script onReady={() => {
                dispatch(setIsCaptchaReady(true))
            }} src={'/script/gt4.js'}
                    strategy={'afterInteractive'}/>

            <Script onReady={() => {
                dispatch(setIsAnjReady(true))
            }}
                    src="https://a298fc83-6db0-45d8-af73-467936ab9c27.snippet.anjcdn.org/anj-seal.js"
                    strategy={'afterInteractive'}></Script>
        </>
    )
}
